// /leads/:leadId — ek lead ki detail: contact, qualification, appointment,
// latest call, aur Call Again / Mark Invalid. Live: socket (useLeadDetail).
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styles from "./PounceLeadScreens.module.css";
import { Icon, LeadHero, ScreenShell } from "./LeadScreenParts";
import {
  appointmentLabel,
  attemptLabel,
  dialedAttempts,
  formatDuration,
  formatFull,
  formatWhen,
  isHeld,
  qualificationRows,
  useLeadDetail,
  usePouncePermissions,
} from "../pounceLeadShared";
import { callPounceLeadAgain, markPounceLeadInvalid } from "../../../Store/apiStore";
import usePounceAccess, { POUNCE_LOCK_MESSAGE } from "../../../hooks/usePounceAccess";

// Inhe dobara call nahi lagti (server bhi 409 deta hai).
const NO_CALL_STATUSES = ["opted_out", "bad_number"];
// Attempt abhi khatam nahi hua — call lag rahi hai ya chal rahi hai.
const OPEN_ATTEMPT_STATUSES = ["pending", "dialing", "in_progress"];
// Call Again ke baad itni der button band (server ko dial shuru karne ka waqt).
const CALL_LOCK_MS = 15000;

// Team access wala middleware `error` key me jawab deta hai, baaki routes `message` me.
const errorText = (err, fallback) => err?.response?.data?.message || err?.response?.data?.error || fallback;

const ContactRow = ({ icon, href, value }) => {
  const [copied, setCopied] = useState(false);
  return (
    <div className={styles.contactRow}>
      {icon}
      <a className={styles.contactLink} href={href}>
        {value}
      </a>
      <button
        type="button"
        className={styles.iconBtn}
        aria-label={`Copy ${value}`}
        // Clipboard WebView me fail ho sakta hai — tab chup.
        onClick={() =>
          navigator.clipboard
            ?.writeText(value)
            .then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            })
            .catch(() => { })
        }
      >
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
};

const PounceLeadDetail = () => {
  const { leadId } = useParams();
  const navigate = useNavigate();
  // Dobara call / invalid = edit. Support ko sirf tab jab owner ne diya ho.
  const { canEdit } = usePounceAccess();
  const { lead, error, reload } = useLeadDetail(leadId);
  // Team me Call Again / Mark Invalid sirf admin ya "edit agents" wale ke liye (server bhi rokta hai).
  const { canEditAgents } = usePouncePermissions();
  const [busy, setBusy] = useState(false);
  const [confirmInvalid, setConfirmInvalid] = useState(false);
  const [showAllCalls, setShowAllCalls] = useState(false);
  const [notice, setNotice] = useState(null); // { text, error? }

  const calling = lead?.status === "calling";

  // Call Again dabane ke baad server ko dial shuru karne me ek-do second lagte
  // hain, aur tab tak lead ka data purana hi hota hai — button dobara dab jaata.
  // Isliye itni der button band, aur beech me data khud taaza (socket na pahunche to bhi).
  const [callRequested, setCallRequested] = useState(false);
  useEffect(() => {
    if (!callRequested) return undefined;
    const early = setTimeout(reload, 3000);
    const done = setTimeout(() => {
      reload();
      setCallRequested(false);
      setNotice(null);
    }, CALL_LOCK_MS);
    return () => {
      clearTimeout(early);
      clearTimeout(done);
    };
  }, [callRequested, reload]);

  const run = async (action, okText, failText) => {
    setBusy(true);
    setNotice(null);
    try {
      await action(leadId);
      setNotice({ text: okText });
      if (action === callPounceLeadAgain) setCallRequested(true);
      await reload();
    } catch (err) {
      setNotice({ text: errorText(err, failText), error: true });
    } finally {
      setBusy(false);
      setConfirmInvalid(false);
    }
  };

  return (
    <ScreenShell title="Lead Detail" lead={lead} error={error} reload={reload}>
      {(lead) => {
        const rows = qualificationRows(lead.analysis).slice(0, 3);
        const appt = lead.appointment;
        const apptLabel = appointmentLabel(appt);
        const calls = dialedAttempts(lead); // naya pehle
        const latest = calls[0];
        const answered = (lead.attempts || []).some((a) => a.outcome === "answered");
        const openCall = (a) => navigate(`/leads/${leadId}/transcript?attempt=${a.id}`);
        const held = isHeld(lead);
        // Call chal rahi hai ya bas lagne wali hai — tab dobara call nahi:
        //   • koi attempt abhi khula hai (dialing / in progress)
        //   • lead calling me hai aur nextAttemptAt NULL (dial ho chuka) ya nikal chuka (cron abhi uthayega)
        // Hold wali lead (Pounce off) alag hai — wo ruki hai, chal nahi rahi.
        const openAttempt = (lead.attempts || []).some((a) => OPEN_ATTEMPT_STATUSES.includes(a.status));
        const dueOrDialing =
          calling && !held && (!lead.nextAttemptAt || new Date(lead.nextAttemptAt).getTime() <= Date.now());
        const canCall =
          !NO_CALL_STATUSES.includes(lead.status) && !openAttempt && !dueOrDialing && !callRequested;

        return (
          <>
            <LeadHero lead={lead} />

            {(lead.phone || lead.email) && (
              <section className={styles.card} aria-label="Contact">
                {lead.phone && <ContactRow icon={Icon.phone} href={`tel:${lead.phone}`} value={lead.phone} />}
                {lead.email && <ContactRow icon={Icon.mail} href={`mailto:${lead.email}`} value={lead.email} />}
              </section>
            )}

            {lead.inquiry && (
              <section className={styles.card} aria-label="Inquiry">
                <p className={styles.muted}>Asked about: {lead.inquiry}</p>
              </section>
            )}

            {/* Sirf tab jab call uthi ho — tabhi qualification hoti hai. Tap → Screen 4. */}
            {answered && (
              <button
                type="button"
                className={`${styles.card} ${styles.cardTap}`}
                onClick={() => navigate(`/leads/${leadId}/activity`)}
              >
                <span className={styles.cardHead}>
                  {Icon.spark}
                  <span className={styles.cardTitle}>Qualification Summary</span>
                  <span className={styles.chevron}>{Icon.chevron}</span>
                </span>
                {rows.length ? (
                  rows.map((r) => (
                    <span key={r.label} className={styles.kv}>
                      <span className={styles.kvLabel}>{r.label}</span>
                      <span className={styles.kvValue}>{r.value}</span>
                    </span>
                  ))
                ) : (
                  <span className={styles.muted}>
                    {lead.analysis === null ? "Answers aren't available for this call yet." : "No answers captured."}
                  </span>
                )}
              </button>
            )}

            {appt && (
              <section className={styles.card} aria-label="Appointment">
                <div className={styles.cardHead}>
                  {Icon.calendar}
                  <h2 className={styles.cardTitle}>Appointment Booked</h2>
                </div>
                {appt.title && <p className={styles.apptTitle}>{appt.title}</p>}
                {apptLabel.date && (
                  <div className={styles.iconLine}>
                    {Icon.calendar}
                    {apptLabel.date}
                  </div>
                )}
                {apptLabel.time && (
                  <div className={styles.iconLine}>
                    {Icon.clock}
                    {apptLabel.time}
                  </div>
                )}
                {appt.provider && (
                  <div className={styles.iconLine}>
                    {Icon.calendar}
                    {appt.provider}
                  </div>
                )}
              </section>
            )}

            <section className={styles.card} aria-label="Latest call">
              <div className={styles.cardHead}>
                {Icon.phone}
                <h2 className={styles.cardTitle}>Latest Call</h2>
                {latest?.durationSec > 0 && <span className={styles.cardAside}>{formatDuration(latest.durationSec)}</span>}
              </div>
              {latest ? (
                <p className={styles.callMeta}>
                  {attemptLabel(latest)} ·{" "}
                  {formatFull(latest.endedAt || latest.startedAt || latest.createdAt, lead.displayTimezone)}
                  {latest.attemptNo > 1 && ` · Attempt ${latest.attemptNo}`}
                </p>
              ) : (
                <p className={styles.callMeta}>Not called yet</p>
              )}
              {/* {calling && lead.nextAttemptAt && (
                <p className={styles.callMeta}>Next attempt {formatWhen(lead.nextAttemptAt, lead.displayTimezone)}</p>
              )} */}
              {calling && lead.nextAttemptAt && (() => {
                const isPast = new Date(lead.nextAttemptAt) < new Date();
                return isPast
                  ? <p className={styles.callMeta}>Next attempt: pending (will call at next available business window)</p>
                  : <p className={styles.callMeta}>Next attempt {formatWhen(lead.nextAttemptAt, lead.displayTimezone)}</p>;
              })()}
              {(held || !calling) && lead.closedReason && <p className={styles.callMeta}>{lead.closedReason}</p>}
              {/* Isi (latest) call ka transcript — jo card me dikh rahi hai. */}
              {latest && (latest.transcript || latest.recordingUrl) && (
                <button type="button" className={styles.rowLink} onClick={() => openCall(latest)}>
                  {Icon.file}
                  <span>View Transcript</span>
                  {Icon.chevron}
                </button>
              )}

              {/* Call Again ke baad ek lead ki kai calls — har call ka apna transcript. */}
              {calls.length > 1 && (
                <>
                  <button
                    type="button"
                    className={styles.linkBtn}
                    aria-expanded={showAllCalls}
                    onClick={() => setShowAllCalls((v) => !v)}
                  >
                    {showAllCalls ? "Hide calls" : `View all calls (${calls.length})`}
                  </button>
                  {showAllCalls && (
                    <ul className={styles.callList}>
                      {calls.map((a) => (
                        <li key={a.id}>
                          <button type="button" className={styles.callRow} onClick={() => openCall(a)}>
                            <span className={styles.callRowText}>
                              <span className={styles.callRowTitle}>
                                Call {a.attemptNo} · {attemptLabel(a)}
                                {a.durationSec > 0 && ` · ${formatDuration(a.durationSec)}`}
                              </span>
                              <span className={styles.callRowMeta}>
                                {formatFull(a.endedAt || a.startedAt || a.createdAt, lead.displayTimezone)}
                              </span>
                            </span>
                            {Icon.chevron}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </section>

            {notice && (
              <p className={`${styles.notice} ${notice.error ? styles.noticeError : ""}`} role="status">
                {notice.text}
              </p>
            )}

            {/* Team me bina "editvcvc agents" ke ye actions chalte hi nahi (server 403) — bar hi nahi. */}
            {canEditAgents && (
            <nav className={styles.actionBar} aria-label="Lead actions">
              <button
                type="button"
                className={`${styles.actionBtn} ${styles.actionPrimary}`}
                disabled={busy || !canCall || !canEdit}
                title={canEdit ? undefined : POUNCE_LOCK_MESSAGE}
                onClick={() => run(callPounceLeadAgain, "Calling now.", "We couldn't start the call.")}
              >
                {Icon.phone}
                Call Again
              </button>
              {/* Pounce ka CRM link abhi nahi bana — button design ke liye, band. */}
              {/* <button type="button" className={`${styles.actionBtn} ${styles.actionGhost}`} disabled title="Coming soon">
                {Icon.redo}
                Sync to CRM
              </button> */}
              <button
                type="button"
                className={`${styles.actionBtn} ${styles.actionDanger}`}
                disabled={busy || lead.status === "bad_number" || !canEdit}
                title={canEdit ? undefined : POUNCE_LOCK_MESSAGE}
                // Do tap: galti se lead band na ho (WebView me confirm() bharosemand nahi).
                onClick={() =>
                  confirmInvalid
                    ? run(markPounceLeadInvalid, "Lead marked invalid.", "We couldn't update this lead.")
                    : setConfirmInvalid(true)
                }
                onBlur={() => setConfirmInvalid(false)}
              >
                {Icon.ban}
                {confirmInvalid ? "Tap to confirm" : "Mark Invalid"}
              </button>
            </nav>
            )}
          </>
        );
      }}
    </ScreenShell>
  );
};

export default PounceLeadDetail;
